import { formatDate, formatToUSD } from "../../network/helper";
import { getTargetDate } from '../../control/Constant';
import React, {useEffect,useState, useCallback, useRef} from "react"

// Custom Input Component that maintains focus
const FocusPreservingInput = ({ initialValue, onValueChange, style, ...props }) => {
    const [localValue, setLocalValue] = useState(initialValue || '')
    const inputRef = useRef(null)
    const timeoutRef = useRef(null)
    
    // Update local value when initial value changes from parent
    useEffect(() => {
        if (initialValue !== localValue && document.activeElement !== inputRef.current) {
            setLocalValue(initialValue || '')
        }
    }, [initialValue])
    
    const handleChange = (e) => {
        const newValue = e.target.value
        setLocalValue(newValue)
        
        // Clear existing timeout
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        
        // Debounce the parent update
        timeoutRef.current = setTimeout(() => {
            onValueChange(newValue)
        }, 300)
    }
    
    const handleBlur = () => {
        // Immediate update on blur
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current)
        }
        onValueChange(localValue)
    }
    
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current)
            }
        }
    }, [])
    
    return (
        <input
            ref={inputRef}
            type="text"
            value={localValue}
            onChange={handleChange}
            onBlur={handleBlur}
            style={style}
            {...props}
        />
    )
}

const colColors = ["FFFFFF","FF9F9F","FFC000","8AE777","FF99FF","00B0F0","00FFFF","FFFFFF",];

const AttorneyReport = (props) => {
    const [editableData, setEditableData] = useState({})
    const inputRefs = useRef({})

    // Define which rows should be editable
    const editableRows = [
        'mtdInitialsHeld',
        'mtdInitialsRetained', 
        'mtdRevenueRetained',
        'mtdRevenueCollected',
        'mtdRevenueCollectedFromInitials',
        'mtdRevenueCollectedNotFromInitials'
    ]

    // Get current user ID for user-specific storage
    const getCurrentUserId = () => {
        return sessionStorage.getItem('loggedUserId') || sessionStorage.getItem('SessPrimaryUserId') || ''
    }

    // Load saved data from localStorage on component mount (user-specific)
    useEffect(() => {
        const userId = getCurrentUserId()
        const savedData = localStorage.getItem(`attorneyReportEditableData_${userId}`)
        if (savedData) {
            setEditableData(JSON.parse(savedData))
        }
    }, [])

    // Handle input changes with immediate state update
    const handleCellChange = useCallback((rowKey, columnKey, value) => {
        setEditableData(prevData => {
            const newData = {
                ...prevData,
                [rowKey]: {
                    ...prevData[rowKey],
                    [columnKey]: value
                }
            }
            
            // Save to localStorage (user-specific)
            const userId = getCurrentUserId()
            localStorage.setItem(`attorneyReportEditableData_${userId}`, JSON.stringify(newData))
            return newData
        })
    }, [])

    // Helper function to parse numeric values from strings
    const parseNumericValue = (value) => {
        if (!value) return 0
        const str = String(value).trim()
        // Bracket notation like $(345) means negative
        const isNegative = str.includes('(') && str.includes(')')
        const cleanValue = str.replace(/[$,()%]/g, '').trim()
        const num = parseFloat(cleanValue)
        if (isNaN(num)) return 0
        return isNegative ? -num : num
    }

    // Helper function to format numbers back to display format
    const formatDisplayValue = (value, isPercentage = false) => {
        if (!value && value !== 0) return ''
        if (isPercentage) {
            return `${value}%`
        }
        if (value < 0) {
            return `$(${Math.abs(value).toLocaleString()})`
        }
        return `$${value.toLocaleString()}`
    }

    // Get the display value for a cell (editable data takes precedence)
    const getCellValue = (rowKey, columnKey, originalValue) => {
        if (editableData[rowKey] && editableData[rowKey][columnKey] !== undefined) {
            return editableData[rowKey][columnKey]
        }
        return originalValue
    }

    // Calculate totals automatically
    const calculateTotal = useCallback((rowKey, columnKey) => {
        if (columnKey !== 'total') return null
        
        const columns = ['rajiv', 'aaron', 'tim', 'ama', 'seminar', 'refunds', 'other']
        let total = 0
        
        columns.forEach(col => {
            // Get value directly from editableData or original data
            let value = 0
            if (editableData[rowKey] && editableData[rowKey][col] !== undefined) {
                value = editableData[rowKey][col]
            } else {
                value = props.dashboardData?.sheetData?.[rowKey]?.[col] || 0
            }
            const parsed = parseNumericValue(value)
            // refunds always subtract from total
            total += col === 'refunds' ? -Math.abs(parsed) : parsed
        })
        
        return total
    }, [editableData, props.dashboardData])

    // Calculate all dependent fields automatically
    const calculateDependentFields = useCallback((rowKey, columnKey, originalValue) => {
        // 1. Closed Percentage = (MTD Initials Retained / MTD Initials Held) * 100
        if (rowKey === 'closedPercentage') {
            let retainedValue = 0
            if (editableData['mtdInitialsRetained'] && editableData['mtdInitialsRetained'][columnKey] !== undefined) {
                retainedValue = editableData['mtdInitialsRetained'][columnKey]
            } else {
                retainedValue = props.dashboardData?.sheetData?.mtdInitialsRetained?.[columnKey] || 0
            }
            
            let heldValue = 0
            if (editableData['mtdInitialsHeld'] && editableData['mtdInitialsHeld'][columnKey] !== undefined) {
                heldValue = editableData['mtdInitialsHeld'][columnKey]
            } else {
                heldValue = props.dashboardData?.sheetData?.mtdInitialsHeld?.[columnKey] || 0
            }
            
            const retained = parseNumericValue(retainedValue)
            const held = parseNumericValue(heldValue)
            
            if (held === 0) return '0%'
            const percentage = ((retained / held) * 100).toFixed(1)
            return `${percentage}%`
        }

        // 2. MTD Average Collection per Retained Client = MTD Revenue Retained / MTD Initials Retained
        if (rowKey === 'mtdAvgColPerRetainedClient') {
            let revenueRetainedValue = 0
            if (editableData['mtdRevenueRetained'] && editableData['mtdRevenueRetained'][columnKey] !== undefined) {
                revenueRetainedValue = editableData['mtdRevenueRetained'][columnKey]
            } else {
                revenueRetainedValue = props.dashboardData?.sheetData?.mtdRevenueRetained?.[columnKey] || 0
            }
            
            let initialsRetainedValue = 0
            if (editableData['mtdInitialsRetained'] && editableData['mtdInitialsRetained'][columnKey] !== undefined) {
                initialsRetainedValue = editableData['mtdInitialsRetained'][columnKey]
            } else {
                initialsRetainedValue = props.dashboardData?.sheetData?.mtdInitialsRetained?.[columnKey] || 0
            }
            
            const revenueRetained = parseNumericValue(revenueRetainedValue)
            const initialsRetained = parseNumericValue(initialsRetainedValue)
            
            if (initialsRetained === 0) return '$0'
            const avgCollection = (revenueRetained / initialsRetained)
            return formatDisplayValue(avgCollection)
        }

        // 3. MTD TOTAL COLLECTED = MTD Revenue Collected from initials + MTD Revenue Collected not from initials
        if (rowKey === 'mtdTotalCollected') {
            let fromInitialsValue = 0
            if (editableData['mtdRevenueCollectedFromInitials'] && editableData['mtdRevenueCollectedFromInitials'][columnKey] !== undefined) {
                fromInitialsValue = editableData['mtdRevenueCollectedFromInitials'][columnKey]
            } else {
                fromInitialsValue = props.dashboardData?.sheetData?.mtdRevenueCollectedFromInitials?.[columnKey] || 0
            }
            
            let notFromInitialsValue = 0
            if (editableData['mtdRevenueCollectedNotFromInitials'] && editableData['mtdRevenueCollectedNotFromInitials'][columnKey] !== undefined) {
                notFromInitialsValue = editableData['mtdRevenueCollectedNotFromInitials'][columnKey]
            } else {
                notFromInitialsValue = props.dashboardData?.sheetData?.mtdRevenueCollectedNotFromInitials?.[columnKey] || 0
            }
            
            const fromInitials = parseNumericValue(fromInitialsValue)
            const notFromInitials = parseNumericValue(notFromInitialsValue)
            const totalCollected = fromInitials + notFromInitials
            
            return formatDisplayValue(totalCollected)
        }

        // 4. MTD Variance = MTD TOTAL COLLECTED - MTD Goal
        if (rowKey === 'mtdVariance') {
            // Get MTD Total Collected (calculated)
            let totalCollectedValue = 0
            let fromInitialsValue = 0
            if (editableData['mtdRevenueCollectedFromInitials'] && editableData['mtdRevenueCollectedFromInitials'][columnKey] !== undefined) {
                fromInitialsValue = editableData['mtdRevenueCollectedFromInitials'][columnKey]
            } else {
                fromInitialsValue = props.dashboardData?.sheetData?.mtdRevenueCollectedFromInitials?.[columnKey] || 0
            }
            
            let notFromInitialsValue = 0
            if (editableData['mtdRevenueCollectedNotFromInitials'] && editableData['mtdRevenueCollectedNotFromInitials'][columnKey] !== undefined) {
                notFromInitialsValue = editableData['mtdRevenueCollectedNotFromInitials'][columnKey]
            } else {
                notFromInitialsValue = props.dashboardData?.sheetData?.mtdRevenueCollectedNotFromInitials?.[columnKey] || 0
            }
            
            totalCollectedValue = parseNumericValue(fromInitialsValue) + parseNumericValue(notFromInitialsValue)
            
            // Get MTD Goal
            const goalValue = props.dashboardData?.sheetData?.mtdGoal?.[columnKey] || 0
            const goal = parseNumericValue(goalValue)
            
            const variance = totalCollectedValue - goal
            
            // Format with brackets for negative values
            if (variance < 0) {
                return `$(${Math.abs(variance).toLocaleString()})`
            }
            return formatDisplayValue(variance)
        }

        // 5. MTD Variance Percentage = (MTD TOTAL COLLECTED / MTD Goal) * 100
        if (rowKey === 'mtdVariancePercentage') {
            // Get MTD Total Collected (calculated)
            let totalCollectedValue = 0
            let fromInitialsValue = 0
            if (editableData['mtdRevenueCollectedFromInitials'] && editableData['mtdRevenueCollectedFromInitials'][columnKey] !== undefined) {
                fromInitialsValue = editableData['mtdRevenueCollectedFromInitials'][columnKey]
            } else {
                fromInitialsValue = props.dashboardData?.sheetData?.mtdRevenueCollectedFromInitials?.[columnKey] || 0
            }
            
            let notFromInitialsValue = 0
            if (editableData['mtdRevenueCollectedNotFromInitials'] && editableData['mtdRevenueCollectedNotFromInitials'][columnKey] !== undefined) {
                notFromInitialsValue = editableData['mtdRevenueCollectedNotFromInitials'][columnKey]
            } else {
                notFromInitialsValue = props.dashboardData?.sheetData?.mtdRevenueCollectedNotFromInitials?.[columnKey] || 0
            }
            
            totalCollectedValue = parseNumericValue(fromInitialsValue) + parseNumericValue(notFromInitialsValue)
            
            // Get MTD Goal
            const goalValue = props.dashboardData?.sheetData?.mtdGoal?.[columnKey] || 0
            const goal = parseNumericValue(goalValue)
            
            if (goal === 0) return '0%'
            const variancePercentage = ((totalCollectedValue / goal) * 100).toFixed(1)
            return `${variancePercentage}%`
        }

        return null
    }, [editableData, props.dashboardData])

    // Enhanced getCellValue with all calculations
    const getCellValueWithCalculations = useCallback((rowKey, columnKey, originalValue) => {
        // Check for manual edits first
        if (editableData[rowKey] && editableData[rowKey][columnKey] !== undefined) {
            return editableData[rowKey][columnKey]
        }
        
        // Auto-calculate totals for editable rows
        if (editableRows.includes(rowKey) && columnKey === 'total') {
            const calculatedTotal = calculateTotal(rowKey, columnKey)
            if (calculatedTotal !== null) {
                return formatDisplayValue(calculatedTotal)
            }
        }
        
        // Auto-calculate all dependent fields
        const calculatedValue = calculateDependentFields(rowKey, columnKey, originalValue)
        if (calculatedValue !== null) {
            return calculatedValue
        }
        
        return originalValue
    }, [editableData, calculateTotal, calculateDependentFields])

    // EditableCell component using custom focus-preserving input
    const EditableCell = ({ rowKey, columnKey, data, className, isColumnEditable, displayValue }) => {
        const inputValue = getCellValue(rowKey, columnKey, data?.[columnKey] || '')
        
        const handleValueChange = useCallback((value) => {
            handleCellChange(rowKey, columnKey, value)
        }, [rowKey, columnKey])
        
        return (
            <td className={className}>
                {isColumnEditable ? (
                    <FocusPreservingInput
                        initialValue={inputValue}
                        onValueChange={handleValueChange}
                        style={{
                            width: '100%',
                            border: 'none',
                            background: 'transparent',
                            fontSize: '13px',
                            padding: '2px 4px',
                            fontFamily: 'inherit'
                        }}
                    />
                ) : (
                    displayValue
                )}
            </td>
        )
    }

    const EditableRow = ({ label, data, rowKey }) => {
        const isEditable = editableRows.includes(rowKey)
        
        return (
            <tr>
                <td className="left">{label}</td>
                {['total', 'rajiv', 'aaron', 'tim', 'ama', 'seminar', 'refunds', 'other'].map(columnKey => {
                    const displayValue = getCellValueWithCalculations(rowKey, columnKey, data?.[columnKey] || '')
                    const isColumnEditable = isEditable && columnKey !== 'total' // Don't allow editing total column
                    
                    return (
                        <EditableCell
                            key={`${rowKey}-${columnKey}`}
                            rowKey={rowKey}
                            columnKey={columnKey}
                            data={data}
                            className={columnKey}
                            isColumnEditable={isColumnEditable}
                            displayValue={displayValue}
                        />
                    )
                })}
            </tr>
        )
    }
            
      function RevenueDashboard({selectedMonth,selectedYear,dashboardData}){
            const { sheetData, revenueCollected } = dashboardData;
            const monthsList = ['January','February','March','April','May','June','July','August','September','October','November','December'];
            const formatDate = (date) => {
                const yyyy = date.getFullYear();
                const mm = String(date.getMonth() + 1).padStart(2, "0");
                const dd = String(date.getDate()).padStart(2, "0");
                return `${mm}/${dd}/${yyyy}`;
              };
              
              const yesterDaysDate = getTargetDate(selectedMonth, selectedYear, 'MM/DD/YYYY');
            
            return(
                <div className="sheet">
                {/* Top Table */}
                <table className="topTable attorney-report">
                  <thead>
                    <tr>
                      <th>{monthsList[Number(selectedMonth) - 1]}</th>
                      <th>Total</th>
                      <th className="rajiv">RAJIV</th>
                      <th className="aaron">AARON</th>
                      <th className="tim">TIM</th>
                      <th className="ama">AMA</th>
                      <th className="seminar">Seminar/Initial/Radio Sponsorship</th>
                      <th className="refunds">Refunds</th>
                      <th className="other">Other</th>
                      
                    </tr>
                  </thead>
                  <tbody>
                    <EditableRow label="MTD Initials Held" data={sheetData?.mtdInitialsHeld} rowKey="mtdInitialsHeld"/>
                    <EditableRow label="MTD Initials Retained" data={sheetData?.mtdInitialsRetained} rowKey="mtdInitialsRetained"/>
                    <EditableRow label="Closed percentage" data={sheetData?.closedPercentage} rowKey="closedPercentage"/>
                    <EditableRow label="MTD Revenue Retained" data={sheetData?.mtdRevenueRetained} rowKey="mtdRevenueRetained"/>
                    <EditableRow label="YTD Revenue Retained" data={sheetData?.mtdRevenueCollected} rowKey="mtdRevenueCollected"/>
                    <EditableRow label="MTD Revenue Collected from initials" data={sheetData?.mtdRevenueCollectedFromInitials} rowKey="mtdRevenueCollectedFromInitials"/>
                    <EditableRow label="MTD Revenue Collected not from initials" data={sheetData?.mtdRevenueCollectedNotFromInitials} rowKey="mtdRevenueCollectedNotFromInitials"/>
                    <EditableRow label="MTD Average Collection per Retained Client" data={sheetData?.mtdAvgColPerRetainedClient} rowKey="mtdAvgColPerRetainedClient"/>
                    <EditableRow label="MTD TOTAL COLLECTED" data={sheetData?.mtdTotalCollected} rowKey="mtdTotalCollected"/>
                    <EditableRow label="MTD Goal" data={sheetData?.mtdGoal} rowKey="mtdGoal"/>
                    <EditableRow label="MTD Variance" data={sheetData?.mtdVariance} rowKey="mtdVariance"/>
                    <EditableRow label="MTD Variance Percentage" data={sheetData?.mtdVariancePercentage} rowKey="mtdVariancePercentage"/>
                  </tbody>
                </table>
          
                {/* Revenue Table */}
                {/* <h3 className="sectionTitle">{""}</h3> */}
                <table className="bottomTable attorney-report">
                    <thead>
                    <tr>
                    <th colSpan={1}>Revenues Collected</th>
                       
                        <th colSpan={2}>Day</th>
                        <th colSpan={2}>Week</th>
                        <th colSpan={2}>Month</th>
                        <th colSpan={2}>YTD</th>
                    </tr>
                    <tr className="trBgWhite">
                        <th>{yesterDaysDate}</th>
                        <th>Daily Goal</th>
                        <th>Yesterday Actual</th>

                        <th>Weekly Goal</th>
                        <th>WTD Actual</th>

                        <th>Monthly Goal</th>
                        <th>MTD Actual</th>

                        <th>Yearly Goal</th>
                        <th>YTD Actual</th>
                    </tr>
                    </thead>
                    <tbody>
                    {Object.entries(revenueCollected).map(([key, val],index) => (
                        
                        <tr key={key} className={`color${index}`}>
                            <td className={key}>{(key=="Seminar")?"Seminar/Radio Sponsorship/ICF":key}</td>

                            <td>{val?.daily}</td>
                            <td>{val?.yesterday}</td>

                            <td>{val?.weekly}</td>
                            <td>{val?.wtd}</td>

                            <td>{val?.monthly}</td>
                            <td>{val?.mtd}</td>

                            <td>{val?.yearly}</td>
                            <td>{val?.ytd}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>

              </div>
            )
            }
    return (
        <>
        {/* <MonthlyCollection attorneyMonthlyReport={props?.attorneyMonthlyReport} selectedMonth={props?.selectedMonth} selectedYear={props?.selectedYear} /> */}
        <RevenueDashboard selectedMonth={props?.selectedMonth} selectedYear={props?.selectedYear} dashboardData={props?.dashboardData}/>
        {/* <RevenuesCollection revenueData={props?.revenueData} /> */}
        </>
    )
};







const MonthlyCollection = ({ attorneyMonthlyReport, selectedMonth, selectedYear }) => {
    const monthsList = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const attorneyTableData = [
        [
        `${monthsList[Number(selectedMonth) - 1]?.toUpperCase()}-${String(selectedYear)?.substr(2, 2)}`,
        "MTD Initials Held",
        "MTD Initials Retained",
        "Closed percentage",
        "MTD Revenue Retained",
        "YTD Revenue Retained",
        "MTD Revenue Collected from initials",
        "MTD Revenue collected not from initials",
        "MTD Average Collection per Retained Client",
        "MTD TOTAL COLLECTED",
        "MTD Goal",
        "MTD Variance",
        "MTD Variance Percentage",

        ],
        ...attorneyMonthlyReport,
        attorneyMonthlyReport?.reduce((tempFinal, curValue) => {
            for(let i = 1; i <= 7; i++) {
                tempFinal[i] += (curValue?.[i] || 0);
            }
            return tempFinal;
        }, ["TOTAL", 0, 0, 0, 0, 0, 0, 0]),
    ];

    return (
        <>
        {/* <div id="attorneyReport" className="flex items-center justify-center p-2">
            <div className="w-full overflow-x-auto shadow-xl">
            <table className="w-full text-sm text-left rtl:text-right border-[20px] border-black text-black">
                <thead className="uppercase text-black">
                <tr className="bg-white">
                    {attorneyTableData.map((ele, index) => {
                        return (
                            <th
                            className={`px-6 py-3 text-center min-w-[100px] border border-black bg-[#${colColors[index]}]`}
                            key={ele?.[0]}
                            scope="col"
                            >
                            {ele?.[0]}
                            </th>
                        );
                    })}
                </tr>
                </thead>
                <tbody>
                {attorneyTableData[0].map((_, rowIndex) => {
                    if (rowIndex == 0) return "";
                    const eachRowEle = attorneyTableData?.map(
                    (eachAttor, innerIndex) => (
                        <th
                        key={eachAttor?.[rowIndex] + innerIndex}
                        scope="col"
                        className={`px-6 py-3 border border-black bg-[#${colColors[innerIndex]}]`}
                        >
                        {innerIndex == 0 ? eachAttor?.[rowIndex] : (formatToUSD(eachAttor?.[rowIndex], true) || <div className="m-auto">-</div>)}
                        </th>
                    )
                    );

                    return (
                    <tr key={rowIndex} className="bg-white border border-black">
                        {eachRowEle}
                    </tr>
                    );
                })}
                </tbody>
            </table>
            </div>
        </div> */}
        </>
    );
}

const RevenuesCollection = ({ revenueData }) => {
    // First header variable
    const firstHeader = "Revenues Collected";

    // Second header variable
    const secondHeader = {
        Day: ['Daily Goal', 'Yesterday Actual'],
        Week: ['Weekly Goal', 'WTD Actual'],
        Month: ['Monthly Goal', 'MTD Actual'],
        YTD: ['Yearly Goal', 'YTD Actual'],
    };

    // Third header variable (list of lists)
    const dataRows = [
        // ["RAJIV", 7500, null, 37500.00, null, 150000.00, null, 1800000.00, null ],
        // ["AARON", 7500, null, 37500.00, null, 150000.00, null, 1800000.00, null],
        // ["TIM", 7500, null, 37500.00, null, 150000.00, null, 1800000.00, null],
        // ["AMA", 7500, null, 37500.00, null, 150000.00, null, 1800000.00, null],
        // ["Seminar/Initial/Radio Sponsorship", 7500, null, 37500.00, null, 150000.00, null, 1800000.00, null],
        // ["Refunds", 7500, null, 37500.00, null, 150000.00, null, 1800000.00, null],
        ...revenueData,
        revenueData?.reduce((tempFinal, curValue) => {
            for(let i = 1; i <= 8; i++) {
                tempFinal[i] += (curValue?.[i] || 0);
            }
            return tempFinal;
        }, ["TOTAL", 0, 0, 0, 0, 0, 0, 0, 0])
    ];

    const headerColors = {
        RAJIV: 'bg-yellow-400',
        AARON: 'bg-green-400',
        TIM: 'bg-pink-400',
        AMA: 'bg-fuchsia-400',
        'Seminar/Initial/Radio Sponsorship': 'bg-blue-400',
        Refunds: 'bg-cyan-400',
        TOTAL: 'bg-gray-800 text-white',
    };

    return (
        <div className="flex items-center justify-center p-2">
        <div className="w-full overflow-x-auto">
            <table className="w-full text-sm text-left rtl:text-right border-[20px] border-black">
            <thead className="text-center">
                <tr className="bg-white">
                    <th className="px-6 py-3 border font-bold border-black rounded-tl-lg">
                        {firstHeader}
                    </th>
                    {Object.keys(secondHeader).map((mainHeader, index) => (
                        <th
                        key={index}
                        colSpan={secondHeader[mainHeader].length}
                        className="px-6 py-3 font-bold border border-black"
                        >
                        {mainHeader}
                        </th>
                    ))}
                </tr>
                <tr className="bg-white">
                    <th rowSpan="2" className="px-6 py-3 font-bold border border-black rounded-tl-lg">
                        {formatDate()}
                    </th>
                    {Object.values(secondHeader).map((subHeaders) =>
                        subHeaders.map((subHeader, subIndex) => (
                        <th key={subIndex} className="px-6 py-3 font-bold border border-black">
                            {subHeader}
                        </th>
                        ))
                    )}
                </tr>
            </thead>
            <tbody>
                {dataRows.map((row, rowIndex) => (
                <tr key={rowIndex} className={`bg-white border border-black bg-[#${colColors[rowIndex + 1]}]`}>
                    <th
                    scope="row"
                    className={`px-6 py-4 font-bold whitespace-nowrap bg-[#${colColors[rowIndex  + 1]}]`}
                    >
                    {row?.[0]}
                    </th>
                    {row?.slice(1)?.map((cell, cellIndex) => (
                    <td key={cellIndex} className="px-6 py-4 border border-black">
                        <div class="flex justify-between w-full">
                            {formatToUSD(cell, true) || <div className="m-auto">-</div>}
                        </div>
                    </td>
                    ))}
                </tr>
                ))}
            </tbody>
            </table>
        </div>
        </div>
    );
}

export default AttorneyReport;
